<template>
  <div id="position-modal">
    <v-dialog :value="isModalActive" max-width="800" @input="inputModal">
      <v-card
        class="position-modal-card pt-5 pb-2"
        :color="$vuetify.theme.dark ? 'dark_backgroundDarkBlue' : undefined"
      >
        <v-card-text class="py-0 my-0">
          <ApiErrorAlert ref="apiErrorAlert" :error="error" />
          <PositionForm
            ref="positionForm"
            :position.sync="position"
            :loading="loading"
            :editing="editing"
            @changePositionToType="changePositionToType"
          />
        </v-card-text>

        <ModalButtons
          :editing="editing"
          :loading="loading"
          :saveText="$t('views.stocks.stockModal.save')"
          :saveDisabled="saveDisabled"
          :hideDelete="position.type === 'cash'"
          @delete="confirmDelete"
          @cancel="closeModal"
          @save="saveStockPosition"
        />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import { mapState } from 'vuex'
import { pick, get, sortBy } from 'lodash'
import { createPosition, editPosition, deletePosition } from '@/lib/api.js'
import {
  getPositionOrDerivativePositionType,
  isPositionDerivative,
} from '@/lib/positions/positions.js'
import gtag from '@/lib/gtag'
import modalMixin from '@/views/components/core/modalMixin.js'
import PositionModalMixin from '@/views/components/common/positions/PositionModalMixin'
import ApiErrorAlert from '@/views/components/common/ApiErrorAlert'
import PositionForm from '@/views/components/common/positions/PositionForm'

export default {
  components: {
    ApiErrorAlert,
    PositionForm,
  },
  mixins: [modalMixin, PositionModalMixin],
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
    editItem: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    position: null,
    loading: false,
    error: null,
  }),
  computed: {
    ...mapState(['user']),
    saveDisabled() {
      return this.positionNotCompleted(this.position)
    },
  },
  watch: {
    editItem: {
      handler() {
        if (this.editing) {
          this.position = JSON.parse(JSON.stringify(this.editItem))
        } else {
          this.resetForm()
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    resetForm() {
      this.$set(this, 'position', { customMarket: { eod: {}, eodHistorical: { historical: [] } } })
      this.error = null
      this.loading = false
      if (this.$refs.positionForm) this.$refs.positionForm.resetForm()
    },
    async saveStockPosition() {
      await this.savePosition()
    },
    async confirmDelete() {
      if (
        await this.$root.$confirm({
          title: this.$t('views.stocks.stockTable.confirm.title'),
          message: this.$t('views.stocks.stockTable.confirm.message'),
        })
      ) {
        await this.deletePosition()
      }
    },
    async savePosition() {
      this.loading = true

      let position = Object.assign({}, this.position)

      if (!isPositionDerivative(position)) {
        delete position.derivativePositionType
      }

      const positionOrDerivativePositionType = getPositionOrDerivativePositionType(position)
      if (
        ['fund', 'putOption', 'callOption', 'custom'].indexOf(positionOrDerivativePositionType) > -1
      ) {
        delete position.exchange
      }
      if (['fund', 'custom'].indexOf(positionOrDerivativePositionType) > -1) {
        delete position.country
      }
      delete position.legalCountry

      if (!position.customDefaultDividendOriginRetentionPercent) {
        delete position.defaultDividendOriginRetentionPercent
      }
      if (!position.customDefaultDividendReturnPercent) {
        delete position.defaultDividendReturnPercent
      }

      if (position.type === 'custom') position.customMarket.active = true
      else if (position.type === 'cash') position.customMarket.active = false
      if (!get(position, 'customMarket.active', false)) {
        delete position.customMarket.eod
        position.customMarket.eodHistorical.active = false
      }

      if (get(position, 'customMarket.eodHistorical.active', false)) {
        const histMap = position.customMarket.eodHistorical.historical.reduce((accMap, elem) => {
          if (!elem.month) return accMap
          const parsedDate = DateTime.fromFormat(elem.month, 'yyyy-MM')
          if (!parsedDate.isValid) return accMap

          if (!elem.close || parseFloat(elem.close) === 0) return accMap

          delete elem.menu

          accMap.set(elem.month, elem)
          return accMap
        }, new Map())

        const historical = Array.from(histMap.values())
        if (historical && historical.length) {
          position.customMarket.eodHistorical.historical = sortBy(historical, 'month').reverse()
        } else {
          position.customMarket.eodHistorical.active = false
        }
      }
      if (!get(position, 'customMarket.eodHistorical.active', false)) {
        delete position.customMarket.eodHistorical.historical
      }

      try {
        if (this.editing) {
          const editAttributes = {
            stock: [
              'type',
              'derivativePositionType',
              'customMarket',
              'ticker',
              'country',
              'exposureCountry',
              'exchange',
              'name',
              'sector',
              'isin',
              'customDefaultDividendOriginRetentionPercent',
              'defaultDividendOriginRetentionPercent',
              'customDefaultDividendReturnPercent',
              'defaultDividendReturnPercent',
              // 'category',
              'tags',
              'notes',
            ],
            right: [
              'type',
              'derivativePositionType',
              'customMarket',
              'ticker',
              'country',
              'exposureCountry',
              'exchange',
              'name',
              'sector',
              'isin',
              // 'category',
              'tags',
              'notes',
            ],
            fund: [
              'type',
              'derivativePositionType',
              'customMarket',
              'ticker',
              'exposureCountry',
              'manager',
              'name',
              'sector',
              'customDefaultDividendOriginRetentionPercent',
              'defaultDividendOriginRetentionPercent',
              'customDefaultDividendReturnPercent',
              'defaultDividendReturnPercent',
              // 'category',
              'tags',
              'notes',
            ],
            etf: [
              'type',
              'derivativePositionType',
              'customMarket',
              'ticker',
              'manager',
              'country',
              'exposureCountry',
              'exchange',
              'name',
              'sector',
              'isin',
              'customDefaultDividendOriginRetentionPercent',
              'defaultDividendOriginRetentionPercent',
              'customDefaultDividendReturnPercent',
              'defaultDividendReturnPercent',
              // 'category',
              'tags',
              'notes',
            ],
            bond: [
              'type',
              'derivativePositionType',
              'customMarket',
              'ticker',
              'country',
              'exposureCountry',
              'name',
              'sector',
              'isin',
              'customDefaultDividendOriginRetentionPercent',
              'defaultDividendOriginRetentionPercent',
              'customDefaultDividendReturnPercent',
              'defaultDividendReturnPercent',
              // 'category',
              'tags',
              'notes',
            ],
            plan: [
              'type',
              'derivativePositionType',
              'customMarket',
              'ticker',
              'manager',
              'country',
              'exposureCountry',
              'exchange',
              'name',
              'sector',
              'isin',
              'customDefaultDividendOriginRetentionPercent',
              'defaultDividendOriginRetentionPercent',
              'customDefaultDividendReturnPercent',
              'defaultDividendReturnPercent',
              // 'category',
              'tags',
              'notes',
            ],
            putOption: [
              'type',
              'derivativePositionType',
              'customMarket',
              'ticker',
              'country',
              'exposureCountry',
              'name',
              'sector',
              'isin',
              // 'category',
              'tags',
              'notes',
            ],
            callOption: [
              'type',
              'derivativePositionType',
              'customMarket',
              'ticker',
              'country',
              'exposureCountry',
              'name',
              'sector',
              'isin',
              // 'category',
              'tags',
              'notes',
            ],
            crypto: [
              'type',
              'derivativePositionType',
              'customMarket',
              'ticker',
              'name',
              'customDefaultDividendOriginRetentionPercent',
              'defaultDividendOriginRetentionPercent',
              'customDefaultDividendReturnPercent',
              'defaultDividendReturnPercent',
              'tags',
              'notes',
            ],
            index: [
              'type',
              'derivativePositionType',
              'customMarket',
              'ticker',
              'name',
              'tags',
              'notes',
            ],
            commodity: [
              'type',
              'derivativePositionType',
              'customMarket',
              'ticker',
              'name',
              'customDefaultDividendOriginRetentionPercent',
              'defaultDividendOriginRetentionPercent',
              'customDefaultDividendReturnPercent',
              'defaultDividendReturnPercent',
              'tags',
              'notes',
            ],
            cash: ['type', 'derivativePositionType', 'ticker', 'tags', 'notes'],
            custom: [
              'type',
              'ticker',
              'customType',
              'customMarket',
              'name',
              'sector',
              'customDefaultDividendOriginRetentionPercent',
              'defaultDividendOriginRetentionPercent',
              'customDefaultDividendReturnPercent',
              'defaultDividendReturnPercent',
              'tags',
              'notes',
            ],
          }

          position = pick(position, editAttributes[positionOrDerivativePositionType])
          await editPosition(this.position._id, position)
        } else {
          await createPosition(position)
        }
        this.$emit('saved')
        this.closeModal()
        this.$root.$snackbar.success({ text: this.$t('common.saveOk') })

        gtag.pageview({
          pagePath: `/portfolio/${this.editing ? 'edit' : 'create'}/success`,
          pageTitle: `portfolio|${this.editing ? 'edit' : 'create'}|success`,
          pageContentGroup: 'modal',
        })
      } catch (error) {
        this.error = get(error, 'data.name', null)
        if (!this.error) {
          this.$root.$snackbar.error({ text: this.$t('common.saveError') })
        } else {
          this.$refs.apiErrorAlert.scrollToError()
        }

        gtag.pageview({
          pagePath: `/portfolio/${this.editing ? 'edit' : 'create'}/error`,
          pageTitle: `portfolio|${this.editing ? 'edit' : 'create'}|error`,
          pageContentGroup: 'modal',
        })
      }
      this.loading = false
    },
    async changePositionToType(type) {
      this.loading = true
      try {
        await editPosition(this.position._id, { type })
        this.$emit('saved')
        this.closeModal()
        this.$root.$snackbar.success({ text: this.$t('common.saveOk') })
      } catch (error) {
        this.error = get(error, 'data.name', null)
        if (!this.error) {
          this.$root.$snackbar.error({ text: this.$t('common.saveError') })
        } else {
          this.$refs.apiErrorAlert.scrollToError()
        }
      }
      this.loading = false
    },
    async deletePosition() {
      this.loading = true
      try {
        await deletePosition(this.position)
        this.$emit('saved')
        this.closeModal()
        this.$root.$snackbar.success({ text: this.$t('common.deleteOk') })
      } catch (error) {
        this.$root.$snackbar.error({ text: this.$t('common.deleteError') })
      }
      this.loading = false
    },
  },
}
</script>
